<template>
  <div class="phone_package">
    <div v-if="spuList.length" style="height: 100%;">
      <div class="spu_module">
        <span
          v-for="(spu, spuIndex) in spuList"
          :class="{ actived: spu.id === currentSpu.id }"
          :key="spuIndex"
          @click="changeSpu(spu)"
          >{{ spu.name }}</span
        >
      </div>
      <div style="height: calc(100% - 60px); overflow: auto">
        <div class="category_module">
          <p class="title">请选择系统</p>
          <div class="category_list">
            <span
              v-for="(categoryItem, categoryIndex) in categoryList"
              :key="categoryIndex"
              :class="{ actived: categoryItem.id === currentCategory.id }"
              @click="changeCategory(categoryItem)"
              v-show="categoryItem.clientDisplayOpen == 0"
              >{{ categoryItem.version }}</span
            >
          </div>
        </div>
        <div class="sku_module">
          <p class="title">请选择套餐</p>
          <div class="sku_list">
            <div
              class="sku_item"
              v-for="(skuItem, skuIndex) in skuList"
              :key="skuIndex"
              :class="{
                actived:
                  currentSku &&
                  skuItem.id === currentSku.id &&
                  skuItem.stock - skuItem.guardWire > 0,
                noStock: skuItem.stock - skuItem.guardWire <= 0
              }"
              @click="changeSku(skuItem)"
            >
              <span>{{ skuItem.nickName }}</span>
              <span
                >￥<b>{{ calcPrice(skuItem) }}</b></span
              >
              <span>{{ calcAveragePrice(skuItem) }}元/天</span>
            
              <span>库存：{{ skuItem.stock - skuItem.guardWire }}</span>
              <i v-show="getSlogan(skuItem)">{{ getSlogan(skuItem) }}</i>
            </div>
            <div v-if="skuList.length == 0" class="noSkuData">
              <img src="@/assets/noData.png" alt="" />
              <span>暂无套餐数据</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="emptyData">
      <img src="@/assets/noData.png" alt="" />
      <span>暂无商品数据</span>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import HttpService from '@/utils/request'
import API_CONFIG from './api.json'
import { ElMessage } from 'element-plus'

const spuList = ref([])
const categoryList = ref([])
const appList = ref([])
const skuList = ref([])
const currentSpu = ref({})
const currentCategory = ref({})
const currentSku = ref({})
const categoryConfigDetail = ref({})

// 套餐数量相关
const packageNumList = ref([])
const currentPackage = ref(null)

const props = defineProps({
  totalNum: {
    type: Number,
    default: 1
  }
})

onMounted(() => {
  getSpuList()
})

// 获取spu列表
const getSpuList = () => {
  HttpService.post(API_CONFIG.querySpu.url, {}, { headers: { repeatSubmit: false } }).then(
    (res) => {
      spuList.value = res.data.filter((ele) => ele.status == '0' && !ele.hideType.includes('pc'))

      // 默认优先查看是否活动跳转
      currentSpu.value = spuList.value[0]
      initCategory(currentSpu.value)
    }
  )
}

// 获取sku列表
const getSkuList = (category) => {
  HttpService.post(
    API_CONFIG.queryCategoryConfig.url,
    {
      categoryId: currentCategory.value.id,
      spuId: currentSpu.value.id
    },
    { headers: { repeatSubmit: false } }
  ).then((res) => {
    skuList.value = (res.data?.goodsSkuList || []).filter(
      (skuItem) => !skuItem.hideType.includes('pc')&&skuItem.isNoviceExperience=='1'
    )
    appList.value = res.data.appAndGameRecommendList || []
    categoryConfigDetail.value = res.data

    // 默认优先查看是否活动跳转，同时查看是不是有库存，有库存的优先
    let defaultSku = null
    // 首先查找有库存的默认值
    if (skuList.value && skuList.value.length > 0) {
      defaultSku = skuList.value.find(
        (ele) => ele.isDefault == '0' && ele.stock - ele.guardWire > 0
      )
      // 如果没有找到有库存的默认值，则选择有库存的第一个
      if (!defaultSku) {
        defaultSku = skuList.value.find((ele) => ele.stock - ele.guardWire > 0)
      }
      // 如果还是没有找到有库存的，则选择第一个（不管是否有库存）
      if (!defaultSku) {
        defaultSku = null
      }
    }

    currentSku.value = defaultSku
  })
}

// 切换spu
const changeSpu = (spu) => {
  currentSpu.value = spu
  initCategory(spu)
  // 重置套餐数量
  packageNumList.value = []
  currentPackage.value = null
}

// 初始设置规格
const initCategory = (spu) => {
  if (!spu) return
  categoryList.value = (spu.goodsCategoryList || []).filter((ele) => ele.clientDisplayOpen == '0').sort((a, b) => a.sort - b.sort)

  // 默认优先查看是否活动跳转
  currentCategory.value = (categoryList.value || []).find((ele) => ele.isDefault == '0') || categoryList.value[0]

  getSkuList()
}

// 切换规格
const changeCategory = (category) => {
  currentCategory.value = category
  getSkuList()
  // 重置套餐数量
  packageNumList.value = []
  currentPackage.value = null
}

// 切换sku
const changeSku = (sku) => {
  if (sku.stock - sku.guardWire <= 0) {
    ElMessage.warning('当前套餐已卖爆，库存不足，如有需要，请联系客服人员，感谢！', {
      width: 300,
      duration: 2000
    })
    return
  }
  currentSku.value = sku
  // 获取套餐阶梯数量
  getPackageNumList(sku.id)
}

// 计算平均数
const calcAveragePrice = (sku) => {
  return (calcPrice(sku) / (sku.seconds / 86400)).toFixed(2)
}

// 获取标语
const getSlogan = (sku) => {
  const activityTime = new Date(sku.goodsSkuPreferential?.preferentialEndTime || '')
  if (activityTime.getTime() > Date.now()) {
    return sku.goodsSkuPreferential?.slogan
  }

  const buyStartTime = new Date(sku.goodsSkuActivity?.buyPcStartDate || '')
  const buyEndTime = new Date(sku.goodsSkuActivity?.buyPcEndDate || '')
  if (buyEndTime.getTime() > Date.now() && buyStartTime.getTime() < Date.now()) {
    return sku.goodsSkuActivity?.buyPcSlogan
  }
  return ''
}

// 计算活动价格，活动时间未到或者没有活动价格，返回普通价格
const calcPrice = (sku) => {
  const activityTime = new Date(
    sku.goodsSkuPreferential?.preferentialEndTime || ""
  );
  if (
    activityTime.getTime() > Date.now() &&
    sku.goodsSkuPreferential?.preferentialPrice
  ) {
    return Number(sku.goodsSkuPreferential?.preferentialPrice || 0).toFixed(2);
  }
  return Number(sku.lowPrice || 0).toFixed(2);
};
</script>

<style lang="scss" scoped>
.phone_package {
  height: 100%;
  padding: 20px;
  .spu_module {
    background: rgba(242, 243, 245, 0.67);
    display: flex;
    flex-direction: row;
    height: 60px;
    padding: 10px 0 0;
    width: 100%;
    span {
      display: inline-block;
      font-size: 20px;
      color: rgba(61, 61, 61, 1);
      font-weight: 500;
      line-height: 60px;
      padding: 0 40px;
      cursor: pointer;
      &.actived {
        background: #ffffff;
        font-size: 24px;
        color: #409eff; 
        border-radius: 8px 8px 0 0;
        position: relative;
        &::before {
          content: '';
          position: absolute;
          display: inline-block;
          height: 2px;
          width: 60%;
          left: 20%;
          bottom: 0;
          background-color: #409eff; 
        }
      }
    }
  }

  .category_module {
    padding: 20px;
    .title {
      line-height: 42px;
      color: #3d3d3d;
      font-size: 14px;
    }
    .category_list {
      display: flex;
      flex-wrap: wrap;
      span {
        display: inline-block;
        min-width: 104px;
        padding: 0 15px;
        height: 28px;
        line-height: 28px;
        text-align: center;
        border-radius: 10px;
        font-size: 14px;
        border: 1px solid #f7cdcd;
        margin-right: 12px;
        margin-bottom: 12px;
        cursor: pointer;
        &.actived {
          background: #409eff; 
          color: #ffffff;
          border-color: #409eff; 
        }
      }
    }
  }
  .sku_module {
    padding: 20px;
    margin-top: 1%;
    .title {
      line-height: 42px;
      color: #3d3d3d;
      font-size: 14px;
    }
    .sku_list {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      gap: 20px;
      .sku_item {
        display: flex;
        flex-direction: column;
        padding: 32px 20px 10px;
        border-radius: 10px;
        background: rgba(255, 248, 233, 1);
        position: relative;
        overflow: hidden;
        cursor: pointer;
        i {
          position: absolute;
          top: 0;
          left: 0;
          display: inline-block;
          font-size: 10px;
          color: #ffffff;
          font-style: normal;
          padding: 2px 5px;
          border-radius: 0 0 10px 0;
          line-height: 16px;
          background: linear-gradient(81deg, #ff8179 0%, #ff0d1d 100%);
        }
        > span {
          text-align: center;
          &:first-child {
            font-weight: 500;
            font-size: 15px;
            color: #333333;
            line-height: 17px;
          }
          &:nth-child(2) {
            font-size: 15px;
            color: #f9620f;
            line-height: 18px;
            margin: 5px 0;
            width: 110px;
            b {
              font-weight: 700;
              font-size: 27px;
              color: #f9620f;
              line-height: 35px;
            }
          }
          &:nth-child(3) {
            font-size: 12px;
            color: #fdb98d;
            line-height: 12px;
          }
        }
        &.actived {
          border: 1px solid #ffa102;
          i {
            background: linear-gradient(81deg, #ffd183 0%, #ffa102 100%);
          }
          > span {
            &:first-child {
              color: rgba(79, 33, 2, 1);
            }
            &:nth-child(2) {
              color: rgba(255, 161, 2, 1);
              b {
                color: rgba(255, 161, 2, 1);
              }
            }
            &:nth-child(3) {
              color: rgba(250, 179, 59, 1);
            }
          }
        }
        &.noStock {
          opacity: 0.5;
        }
      }
    }
  }
  
  .emptyData,
  .noSkuData {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: rgb(144, 147, 153);
  }
  .emptyData {
    img {
      width: 30%;
    }
  }

  .noSkuData {
    font-size: 12px;
    img {
      width: 10%;
    }
  }
}
</style>